import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { asArray, asRecord, runDomainRouterQuery } from './graphql';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type FinancialAidDashboardData = {
  items: Record<string, unknown>[];
  totalCount: number;
  nextToken: string | null;
};

type FinancialAidDetailData = {
  review: Record<string, unknown> | null;
  statusHistory: Record<string, unknown>[];
};

type FinancialAidDetailEntry = {
  status: AsyncStatus;
  error: string | null;
  data: FinancialAidDetailData | null;
};

type FinancialAidLiveState = {
  dashboard: {
    status: AsyncStatus;
    error: string | null;
    data: FinancialAidDashboardData;
  };
  detailByApplicationId: Record<string, FinancialAidDetailEntry>;
};

const initialState: FinancialAidLiveState = {
  dashboard: {
    status: 'idle',
    error: null,
    data: {
      items: [],
      totalCount: 0,
      nextToken: null,
    },
  },
  detailByApplicationId: {},
};

const createEmptyDetail = (): FinancialAidDetailEntry => ({
  status: 'idle',
  error: null,
  data: null,
});

export const fetchFinancialAidDashboard = createAsyncThunk(
  'financialAidLive/fetchDashboard',
  async (): Promise<FinancialAidDashboardData> => {
    const data = asRecord(await runDomainRouterQuery('listFinancialAidApplicationsForReview', {
      input: JSON.stringify({ limit: 100 }),
    }));
    return {
      items: asArray<Record<string, unknown>>(data.items),
      totalCount: Number(data.totalCount ?? 0),
      nextToken: data.nextToken ? String(data.nextToken) : null,
    };
  }
);

export const fetchFinancialAidDetail = createAsyncThunk(
  'financialAidLive/fetchDetail',
  async (applicationId: string): Promise<{ applicationId: string; detail: FinancialAidDetailData }> => {
    const normalizedId = String(applicationId ?? '').trim();

    const review = asRecord(
      await runDomainRouterQuery('getFinancialAidApplicationForReview', { applicationId: normalizedId })
    );

    const historyData = asRecord(
      await runDomainRouterQuery('getFinancialAidStatusHistory', { applicationId: normalizedId })
    );

    return {
      applicationId: normalizedId,
      detail: {
        review,
        statusHistory: asArray<Record<string, unknown>>(historyData.entries),
      },
    };
  }
);

const financialAidLiveSlice = createSlice({
  name: 'financialAidLive',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFinancialAidDashboard.pending, (state) => {
        state.dashboard.status = 'loading';
        state.dashboard.error = null;
      })
      .addCase(fetchFinancialAidDashboard.fulfilled, (state, action: PayloadAction<FinancialAidDashboardData>) => {
        state.dashboard.status = 'succeeded';
        state.dashboard.data = action.payload;
      })
      .addCase(fetchFinancialAidDashboard.rejected, (state, action) => {
        state.dashboard.status = 'failed';
        state.dashboard.error = action.error.message ?? 'Failed to load financial-aid dashboard';
      })
      .addCase(fetchFinancialAidDetail.pending, (state, action) => {
        const applicationId = action.meta.arg;
        const entry = state.detailByApplicationId[applicationId] ?? createEmptyDetail();
        entry.status = 'loading';
        entry.error = null;
        state.detailByApplicationId[applicationId] = entry;
      })
      .addCase(fetchFinancialAidDetail.fulfilled, (state, action) => {
        const { applicationId, detail } = action.payload;
        state.detailByApplicationId[applicationId] = {
          status: 'succeeded',
          error: null,
          data: detail,
        };
      })
      .addCase(fetchFinancialAidDetail.rejected, (state, action) => {
        const applicationId = action.meta.arg;
        const entry = state.detailByApplicationId[applicationId] ?? createEmptyDetail();
        entry.status = 'failed';
        entry.error = action.error.message ?? 'Failed to load financial-aid detail';
        state.detailByApplicationId[applicationId] = entry;
      });
  },
});

export const financialAidLiveReducer = financialAidLiveSlice.reducer;
